import React, { useState } from 'react';
import { TransaksiPembayaran } from '../../types/index.js';
import { Printer, RotateCcw, Search, Calendar, FileText, CheckCircle2, Ban, MessageSquare, CheckCheck, AlertCircle, XCircle } from 'lucide-react';
import { useDialog } from '../ui/DialogProvider.js';
import { useDataTable } from '../../hooks/useDataTable.js';
import { Pagination } from '../ui/Pagination.js';
import { SortableHeader } from '../ui/SortableHeader.js';
import { formatPaymentMethod, getPokokNominal } from '../../utils/paymentMethod.js';

interface CetakBuktiCancelViewProps {
  transaksiList: TransaksiPembayaran[];
  onCancelTransaksi: (id: string, alasan: string) => Promise<void>;
  onShowReceipt: (trx: TransaksiPembayaran) => void;
}

export const CetakBuktiCancelView: React.FC<CetakBuktiCancelViewProps> = ({
  transaksiList,
  onCancelTransaksi,
  onShowReceipt,
}) => {
  const { showAlert, showConfirm, showPrompt } = useDialog();
  const [filterDate, setFilterDate] = useState(new Date().toISOString().slice(0, 10));

  const normalizedData = React.useMemo(() => {
    return (transaksiList || []).map((trx) => ({
      ...trx,
      displayChannel: formatPaymentMethod(trx.channel, trx.metodeBayar),
      displayTotal: getPokokNominal(trx),
    }));
  }, [transaksiList]);

  const {
    searchQuery: search,
    setSearchQuery: setSearch,
    sortBy,
    sortDir,
    handleSort,
    currentPage,
    setCurrentPage,
    rowsPerPage,
    setRowsPerPage,
    totalPages,
    totalRows,
    paginatedData: filteredTrx,
  } = useDataTable<any>({
    data: normalizedData,
    searchFields: ['noTransaksi', 'noKuitansi', 'namaSiswa', 'nis', 'displayChannel'],
    initialSortBy: 'tanggal',
    initialSortDir: 'desc',
  });

  const handleCancel = async (trx: TransaksiPembayaran) => {
    if (trx.status === 'CANCELLED') {
      showAlert('Transaksi ini sudah dibatalkan sebelumnya.', { type: 'warning' });
      return;
    }
    if (await showConfirm(`Yakin ingin membatalkan transaksi ${trx.noKuitansi} (Void)? Saldo atau tagihan akan dikembalikan ke kondisi semula.`, { title: 'Konfirmasi Void Transaksi', type: 'danger' })) {
      const alasanInput = await showPrompt('Masukkan alasan pembatalan (opsional):', { 
        title: 'Alasan Pembatalan / Void', 
        placeholder: 'Contoh: Kesalahan input kasir...' 
      });
      if (alasanInput === null) return; // User membatalkan prompt
      
      const alasan = alasanInput.trim() || 'Kesalahan input kasir';
      await onCancelTransaksi(trx.id, alasan);
    }
  };

  return (
    <div className="space-y-4 animate-in fade-in duration-200">
      {/* Top Header Matching Document Page 12 */}
      <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-xs flex flex-wrap items-center justify-between gap-4">
        <div>
          <h2 className="text-base font-bold text-slate-900">Cetak Bukti & Cancel Pembayaran</h2>
          <p className="text-xs text-slate-500">
            Riwayat transaksi loket, cetak ulang kuitansi resmi, dan pembatalan pembayaran (void)
          </p>
        </div>

        <div className="flex items-center gap-3">
          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              placeholder="Cari No. Kuitansi / NIS / Siswa..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-8 pr-3 py-1.5 text-xs bg-slate-50 border border-slate-300 rounded-md focus:ring-1 focus:ring-sky-500 w-56"
            />
          </div>
        </div>
      </div>

      {/* Main Table Exact from Document Page 12 */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-xs overflow-hidden flex flex-col min-h-[400px]">
        <div className="overflow-x-auto flex-1">
          <table className="w-full text-xs text-left">
            <thead className="bg-slate-50 border-b border-slate-200 text-slate-600 font-bold uppercase sticky top-0 z-10">
              <tr>
                <th className="py-2.5 px-3 w-12 text-center">No</th>
                <SortableHeader label="Tanggal" columnKey="tanggal" currentSortBy={sortBy as string} currentSortDir={sortDir} onSort={handleSort} />
                <SortableHeader label="No. Transaksi" columnKey="noTransaksi" currentSortBy={sortBy as string} currentSortDir={sortDir} onSort={handleSort} className="font-mono" />
                <SortableHeader label="No. Kuitansi" columnKey="noKuitansi" currentSortBy={sortBy as string} currentSortDir={sortDir} onSort={handleSort} className="font-mono" />
                <SortableHeader label="NIS" columnKey="nis" currentSortBy={sortBy as string} currentSortDir={sortDir} onSort={handleSort} />
                <SortableHeader label="Nama Siswa" columnKey="namaSiswa" currentSortBy={sortBy as string} currentSortDir={sortDir} onSort={handleSort} />
                <SortableHeader label="Total Bayar (Rp)" columnKey="displayTotal" currentSortBy={sortBy as string} currentSortDir={sortDir} onSort={handleSort} className="text-right" />
                <SortableHeader label="Metode Bayar" columnKey="displayChannel" currentSortBy={sortBy as string} currentSortDir={sortDir} onSort={handleSort} />
                <SortableHeader label="Kasir" columnKey="kasir" currentSortBy={sortBy as string} currentSortDir={sortDir} onSort={handleSort} />
                <SortableHeader label="Status" columnKey="status" currentSortBy={sortBy as string} currentSortDir={sortDir} onSort={handleSort} className="text-center" />
                <th className="py-2.5 px-4 text-center">Aksi / Kontrol</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filteredTrx.length === 0 ? (
                <tr>
                  <td colSpan={11} className="text-center py-8 text-slate-400">
                    Tidak ada transaksi ditemukan
                  </td>
                </tr>
              ) : (
                filteredTrx.map((trx, idx) => (
                  <tr key={trx.id} className="hover:bg-sky-50/50 transition">
                    <td className="py-2 px-3 text-center text-slate-500 font-mono">{(currentPage - 1) * rowsPerPage + idx + 1}</td>
                    <td className="py-2 px-3 text-slate-700 whitespace-nowrap">
                      {new Date(trx.tanggal).toLocaleString('id-ID', { hour12: false })}
                    </td>
                  <td className="py-2 px-3 font-mono font-bold text-slate-900">{trx.noTransaksi}</td>
                  <td className="py-2 px-3 font-mono font-semibold text-sky-800">{trx.noKuitansi}</td>
                  <td className="py-2 px-3 font-mono text-slate-700">{trx.nis}</td>
                  <td className="py-2 px-4 font-semibold text-slate-900">{trx.namaSiswa}</td>
                  <td className="py-2 px-3 text-right font-mono font-bold text-emerald-700">
                    {trx.displayTotal.toLocaleString('id-ID')}
                  </td>
                  <td className="py-2 px-3">
                    <span className="bg-slate-100 text-slate-800 px-2 py-0.5 rounded text-[10px] font-semibold">
                      {trx.displayChannel}
                    </span>
                  </td>
                  <td className="py-2 px-3 text-slate-600">{trx.kasir}</td>
                  <td className="py-2 px-3 text-center">
                    {trx.status === 'SUCCESS' ? (
                      <div className="flex flex-col items-center gap-0.5">
                        <span className="bg-emerald-100 text-emerald-800 font-bold px-2 py-0.5 rounded text-[10px]">
                          BERHASIL
                        </span>
                        {trx.waNotificationSent ? (
                          <span className="text-[9px] text-emerald-700 font-semibold flex items-center gap-0.5">
                            <CheckCheck className="w-2.5 h-2.5" /> WA Terkirim
                          </span>
                        ) : (
                          <span className="text-[9px] text-slate-400 flex items-center gap-0.5">
                            <MessageSquare className="w-2.5 h-2.5" /> Belum WA
                          </span>
                        )}
                      </div>
                    ) : trx.status === 'PENDING' ? (
                      <span className="bg-amber-100 text-amber-800 font-bold px-2 py-0.5 rounded text-[10px]">
                        PENDING
                      </span>
                    ) : (
                      <span className="bg-rose-100 text-rose-800 font-bold px-2 py-0.5 rounded text-[10px]">
                        {trx.status === 'EXPIRED' ? 'KADALUARSA' : 'DIBATALKAN'}
                      </span>
                    )}
                  </td>
                  <td className="py-2 px-4 text-center">
                    <div className="flex items-center justify-center gap-1.5">
                        <button
                          onClick={() => onShowReceipt(trx)}
                          className="px-2 py-1 bg-sky-50 hover:bg-sky-100 text-sky-700 border border-sky-300 rounded text-[11px] font-medium flex items-center gap-1 transition shadow-xs"
                          title="Buka Kuitansi"
                        >
                          <Printer className="w-3 h-3" />
                          <span>Cetak</span>
                        </button>

                        {trx.status === 'SUCCESS' && (
                          <button
                            onClick={() => onShowReceipt(trx)}
                            className="px-2 py-1 bg-emerald-50 hover:bg-emerald-100 text-emerald-800 border border-emerald-300 rounded text-[11px] font-medium flex items-center gap-1 transition shadow-xs"
                            title="Kirim Pesan WhatsApp"
                          >
                            <MessageSquare className="w-3 h-3" />
                            <span>WA</span>
                          </button>
                        )}

                        <button
                          onClick={() => handleCancel(trx)}
                          disabled={trx.status === 'CANCELLED'}
                          className={`flex items-center gap-1 px-2.5 py-1 text-[11px] font-bold border rounded shadow-xs transition ${
                            trx.status === 'CANCELLED'
                              ? 'bg-slate-100 text-slate-400 border-slate-200 cursor-not-allowed'
                              : 'bg-rose-50 text-rose-700 hover:bg-rose-100 border-rose-300'
                          }`}
                          title="Batalkan Transaksi / Void"
                        >
                          <RotateCcw className="w-3.5 h-3.5" />
                          <span>Cancel</span>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <Pagination 
          currentPage={currentPage}
          totalPages={totalPages}
          totalRows={totalRows}
          rowsPerPage={rowsPerPage}
          onPageChange={setCurrentPage}
          onRowsPerPageChange={setRowsPerPage}
        />
      </div>
    </div>
  );
};
